import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { Modal, Field } from './Extensions';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const Queues = () => {
    const [items, setItems] = useState([]);
    const [exts, setExts] = useState([]);
    const [editing, setEditing] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const [q, e] = await Promise.all([api.get('/queues'), api.get('/extensions')]);
        setItems(q.data); setExts(e.data);
    };
    useEffect(() => { load(); }, []);

    const blank = {
        name: '', extension_number: '', strategy: 'ring_all',
        member_extension_ids: [], max_wait_seconds: 300, max_callers: 20,
        ring_timeout_per_agent: 20, retry_interval: 5,
        announce_position: true, announce_interval: 30,
        enable_callback: false, is_active: true,
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing.id) await api.put(`/queues/${editing.id}`, editing);
            else await api.post('/queues', editing);
            setEditing(null); load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };
    const del = async (id) => {
        if (!window.confirm('Smazat frontu?')) return;
        await api.delete(`/queues/${id}`); load();
    };

    return (
        <>
            <PageHeader
                eyebrow="ACD"
                title="Call Queues"
                description="Inteligentní fronty hovorů s music-on-hold, strategiemi rozdělování a oznámením pozice. Yeastar-style."
                actions={
                    <button onClick={() => setEditing({ ...blank })}
                            data-testid="queue-create-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        <Plus size={14} weight="bold" />Nová fronta
                    </button>
                }
            />
            <Card>
                {items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádné fronty. Yeastar P-Series typicky používá 6xx pro fronty.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]" style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Číslo</th>
                                <th className="text-left">Název</th>
                                <th className="text-left">Strategie</th>
                                <th className="text-center">Agenti</th>
                                <th className="text-center">Max wait</th>
                                <th className="text-center">Callback</th>
                                <th className="text-center">Stav</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((q) => (
                                <tr key={q.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2 font-mono font-bold">{q.extension_number}</td>
                                    <td>{q.name}</td>
                                    <td className="font-mono text-xs">{q.strategy}</td>
                                    <td className="text-center font-mono">{(q.member_extension_ids || []).length}</td>
                                    <td className="text-center font-mono">{q.max_wait_seconds}s</td>
                                    <td className="text-center">
                                        <StatusPill status={q.enable_callback ? 'success' : 'neutral'} label={q.enable_callback ? 'ON' : 'OFF'} />
                                    </td>
                                    <td className="text-center">
                                        <StatusPill status={q.is_active ? 'success' : 'neutral'} label={q.is_active ? 'aktivní' : 'vypnuto'} />
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => setEditing({ ...q })} className="p-1 hover:bg-[var(--bg-surface-hover)]" data-testid={`queue-edit-${q.id}`}><PencilSimple size={14} /></button>
                                        <button onClick={() => del(q.id)} className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1" data-testid={`queue-delete-${q.id}`}><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <Modal wide title={editing.id ? 'Editace fronty' : 'Nová fronta'} onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="grid grid-cols-3 gap-3">
                        <Field label="Název" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
                        <Field label="Číslo (interní)" value={editing.extension_number} onChange={(v) => setEditing({ ...editing, extension_number: v })} required mono />
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: 'var(--text-secondary)' }}>Strategie</label>
                            <select value={editing.strategy} onChange={(e) => setEditing({ ...editing, strategy: e.target.value })}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                {['ring_all', 'round_robin', 'least_recent', 'fewest_calls', 'linear', 'random']
                                    .map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="col-span-3">
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: 'var(--text-secondary)' }}>Agenti / klapky</label>
                            <div className="mt-1 grid grid-cols-4 gap-1 p-2 border max-h-40 overflow-y-auto"
                                 style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                {exts.map((e) => (
                                    <label key={e.id} className="inline-flex items-center gap-2 text-sm">
                                        <input type="checkbox"
                                               checked={(editing.member_extension_ids || []).includes(e.id)}
                                               onChange={(ev) => {
                                                   const cur = editing.member_extension_ids || [];
                                                   const next = ev.target.checked ? [...cur, e.id] : cur.filter((x) => x !== e.id);
                                                   setEditing({ ...editing, member_extension_ids: next });
                                               }} />
                                        <span className="font-mono text-xs">{e.extension}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <Field label="Max wait (s)" value={editing.max_wait_seconds} onChange={(v) => setEditing({ ...editing, max_wait_seconds: parseInt(v || '0', 10) })} type="number" mono />
                        <Field label="Max volajících" value={editing.max_callers} onChange={(v) => setEditing({ ...editing, max_callers: parseInt(v || '0', 10) })} type="number" mono />
                        <Field label="Ring timeout / agent (s)" value={editing.ring_timeout_per_agent} onChange={(v) => setEditing({ ...editing, ring_timeout_per_agent: parseInt(v || '0', 10) })} type="number" mono />
                        <Field label="Retry interval (s)" value={editing.retry_interval} onChange={(v) => setEditing({ ...editing, retry_interval: parseInt(v || '0', 10) })} type="number" mono />
                        <Field label="Announce interval (s)" value={editing.announce_interval} onChange={(v) => setEditing({ ...editing, announce_interval: parseInt(v || '0', 10) })} type="number" mono />
                        <label className="inline-flex items-center gap-2 text-sm mt-7">
                            <input type="checkbox" checked={!!editing.announce_position}
                                   onChange={(e) => setEditing({ ...editing, announce_position: e.target.checked })} />
                            Oznamovat pozici
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm mt-7">
                            <input type="checkbox" checked={!!editing.enable_callback}
                                   onChange={(e) => setEditing({ ...editing, enable_callback: e.target.checked })} />
                            Callback feature
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm mt-7">
                            <input type="checkbox" checked={!!editing.is_active}
                                   onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                            Aktivní
                        </label>
                        <div className="col-span-3 flex justify-end gap-2 mt-3">
                            <button type="button" onClick={() => setEditing(null)} className="px-3 py-2 text-xs font-bold uppercase border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid="queue-save-btn" className="px-3 py-2 text-xs font-bold uppercase" style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>Uložit</button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
};

export default Queues;
